/**
 * The seam (L1).
 *
 * Every call the UI makes arrives here and is routed to exactly one service.
 * This file is also the composition root: the single place that knows which
 * concrete adapter satisfies which port. Nothing above it imports a service, a
 * repository or an adapter directly (rule R1).
 */

import wasmUrl from 'sql.js/dist/sql-wasm.wasm?url';

import { matches } from '../domain/filter-predicate';

import {
  SqliteClient,
  OpfsDbStorage,
  MemoryDbStorage,
  DbPersister,
  DecisionJournal,
  DirectoryPicker,
  FilePicker,
  FileListDirectory,
  DirectoryWalker,
  HandleStore,
  PermissionGate,
  OpfsStore,
  MemoryBlobStore,
  CanvasImageProcessor,
  CanvasPixelReader,
  ModelRunner,
  DisplayProbe,
  BrowserProbe,
  type BlobStore,
  type Capabilities,
  type FsDirectory,
} from '../adapters';
import { HttpFsDirectory } from '../adapters/http-fs';
import {
  ProjectRepository,
  MediaRepository,
  BucketRepository,
  SourceRepository,
  SettingsRepository,
  AssistRepository,
  type AssistMarkers,
} from '../repositories';
import {
  ProjectService,
  SourceService,
  ScanService,
  ThumbnailService,
  PreviewService,
  BucketService,
  RotationService,
  ExportService,
  CacheService,
  AccessGate,
  AssistService,
  systemClock,
  type SourceAvailability,
  type AssistProgress,
} from '../services';
import type {
  Bucket,
  ExportOptions,
  ExportProgress,
  ListMediaOpts,
  MediaFilter,
  MediaItem,
  Page,
  Project,
  ScanProgress,
  SourceWithCount,
  AccessDecision,
} from '../types';

/** Re-exported so the UI never has to reach past the seam for a type (rule R1). */
import { AssistRuns } from './assist-runs';

export type { SourceAvailability, AssistProgress, ReviewOptions } from '../services';

/** What a shoot was told to do about Review Assist. */
export type AssistDecision = 'unasked' | 'deep' | 'declined';

const ASSIST_ALWAYS_KEY = 'assist.always';

/** Per-shoot, so declining one import says nothing about the next. */
const assistDecisionKey = (projectId: number) => `assist.project.${projectId}`;
export type { AssistMarkers } from '../repositories';
export type { Capabilities, BlockReason } from '../adapters';

const DEMO_SOURCE_KEY = 'demo-source-id';

export interface ImportResult {
  project: Project;
  added: number;
  scanned: number;
}

export class M3Cull {
  private constructor(
    private readonly persister: DbPersister,
    private readonly media: MediaRepository,
    private readonly settings: SettingsRepository,
    private readonly sources: SourceService,
    private readonly svc: {
      projects: ProjectService;
      scan: ScanService;
      thumbs: ThumbnailService;
      previews: PreviewService;
      buckets: BucketService;
      rotation: RotationService;
      exporter: ExportService;
      cache: CacheService;
      gate: AccessGate;
      assist: AssistService;
    },
    private readonly picker: DirectoryPicker,
    private readonly filePicker: FilePicker,
    private readonly cache: BlobStore,
    readonly previewTargetPx: number,
    readonly storageIsPersistent: boolean,
  ) {
    this.runs = new AssistRuns(svc.assist, () => persister.flush());
  }

  /** Lives here, not in a component, so a review survives the photographer navigating away. */
  private readonly runs: AssistRuns;

  /* ------------------------------------------------------------------ *
   * boot
   * ------------------------------------------------------------------ */

  static async boot(): Promise<M3Cull> {
    const hasOpfs =
      typeof navigator !== 'undefined' && typeof navigator.storage?.getDirectory === 'function';

    const dbStorage = hasOpfs ? new OpfsDbStorage() : new MemoryDbStorage();
    const client = await SqliteClient.open(dbStorage, { locateFile: () => wasmUrl });
    const persister = new DbPersister(client, dbStorage);

    const projectsRepo = new ProjectRepository(client);
    const mediaRepo = new MediaRepository(client);
    const bucketsRepo = new BucketRepository(client);
    const sourcesRepo = new SourceRepository(client);
    const settingsRepo = new SettingsRepository(client);

    const cache: BlobStore = hasOpfs ? new OpfsStore() : new MemoryBlobStore();
    const images = new CanvasImageProcessor();
    const display = new DisplayProbe().read();

    const sources = new SourceService(
      sourcesRepo,
      new HandleStore(),
      new PermissionGate(),
      persister,
      systemClock,
    );

    const journal = new DecisionJournal();
    const assistRepo = new AssistRepository(client);
    // One reader, two jobs: the thumbnail for screening, the original for focus.
    const pixels = new CanvasPixelReader();
    const cacheService = new CacheService(cache, mediaRepo);
    const persistent = await cacheService.requestPersistence().catch(() => false);

    const thumbs = new ThumbnailService(
      mediaRepo,
      cache,
      images,
      sources.resolve,
      persister,
      systemClock,
    );

    const api = new M3Cull(
      persister,
      mediaRepo,
      settingsRepo,
      sources,
      {
        projects: new ProjectService(projectsRepo, bucketsRepo, persister, systemClock),
        scan: new ScanService(new DirectoryWalker(), mediaRepo, persister, systemClock),
        thumbs,
        previews: new PreviewService(mediaRepo, cache, images, sources.resolve, persister),
        buckets: new BucketService(mediaRepo, bucketsRepo, persister, journal),
        rotation: new RotationService(mediaRepo, persister, journal),
        exporter: new ExportService(mediaRepo, bucketsRepo, sources.resolve),
        cache: cacheService,
        gate: new AccessGate('free'),
        assist: new AssistService(
          assistRepo,
          pixels,
          // Sharpness is measured on the thumbnail, so this makes sure one
          // exists and hands back its bytes. Wired here because the composition
          // root may know about two services; AssistService may not (rule R2).
          async (mediaId) => {
            const item = mediaRepo.byId(mediaId);
            if (!item) return null;

            const key = item.thumbKey ?? (await thumbs.generate(item));
            return key ? cache.get(key) : null;
          },
          persister,
          systemClock,
          // Constructed, not started. The worker is not spawned and the model is
          // not fetched until a shoot is actually reviewed with it, so a visitor
          // who never enables Review Assist never pays for it.
          ModelRunner.supported ? new ModelRunner() : null,
          // Focus, read back from the original at a size where it can be seen.
          // Deliberately not routed through PreviewService: that caches what it
          // renders, and these renders exist only long enough to produce one
          // number. Wired here because the composition root may know about a
          // service and an adapter at once; AssistService may not (rule R2).
          async (mediaId, longEdge) => {
            const item = mediaRepo.byId(mediaId);
            if (!item || item.sourceId === null || item.type !== 'image') return null;

            const directory = await sources.resolve(item.sourceId);
            if (!directory) return null;

            const file = await directory.file(item.relPath);
            return pixels.grayscaleAt(await file.blob(), longEdge);
          },
        ),
      },
      new DirectoryPicker(),
      new FilePicker(),
      cache,
      display.previewTargetPx,
      persistent,
    );

    await api.replayJournal(journal, mediaRepo);
    await api.restoreDemoSource();

    // Never lose a cull to a closed tab.
    window.addEventListener('pagehide', () => void persister.flush());
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') void persister.flush();
    });

    return api;
  }

  /* ------------------------------------------------------------------ *
   * access
   * ------------------------------------------------------------------ */

  access(): Promise<AccessDecision> {
    return this.svc.gate.decide();
  }

  /**
   * What this browser will let m3cull do, and why not when it will not.
   * Async because Brave only answers isBrave() through a promise.
   */
  static capabilities(): Promise<Capabilities> {
    return BrowserProbe.detect();
  }

  /* ------------------------------------------------------------------ *
   * projects
   * ------------------------------------------------------------------ */

  listProjects(): Project[] {
    return this.svc.projects.list();
  }

  getProject(id: number): Project | null {
    return this.svc.projects.get(id);
  }

  openProject(id: number): Project | null {
    return this.svc.projects.open(id);
  }

  renameProject(id: number, name: string): Project | null {
    return this.svc.projects.rename(id, name);
  }

  deleteProject(id: number): void {
    this.svc.projects.delete(id);
  }

  setAutoAdvance(id: number, value: boolean): void {
    this.svc.projects.setAutoAdvance(id, value);
  }

  setLastViewed(projectId: number, mediaId: number | null): void {
    this.svc.projects.setLastViewed(projectId, mediaId);
  }

  /* ------------------------------------------------------------------ *
   * importing
   * ------------------------------------------------------------------ */

  /** Pick a folder, make a project named after it, and scan it. */
  async importFolder(onProgress?: (p: ScanProgress) => void): Promise<ImportResult | null> {
    const picked = await this.picker.pick('read', 'm3cull-import');
    if (!picked) return null;

    const project = this.svc.projects.create(picked.directory.name, this.previewTargetPx);
    const result = await this.scanInto(project.id, picked.directory, picked.handle, onProgress);
    return { project: this.svc.projects.get(project.id)!, ...result };
  }

  /** Add another folder to a project that already exists. */
  async addFolder(
    projectId: number,
    onProgress?: (p: ScanProgress) => void,
  ): Promise<{ added: number; scanned: number } | null> {
    const picked = await this.picker.pick('read', 'm3cull-import');
    if (!picked) return null;
    return this.scanInto(projectId, picked.directory, picked.handle, onProgress);
  }

  /**
   * Pick individual photos rather than a whole folder.
   *
   * Returns null if the dialog was dismissed, or a result with added: 0 when
   * everything picked was a file type m3cull does not handle.
   */
  async importFiles(onProgress?: (p: ScanProgress) => void): Promise<ImportResult | null> {
    const handles = await this.filePicker.pick('m3cull-files');
    if (handles === null) return null;

    const name = this.freeSourceName(0);
    const directory = new FileListDirectory(name, handles);

    const project = this.svc.projects.create(name, this.previewTargetPx);
    const result = await this.scanFilesInto(project.id, directory, onProgress);
    return { project: this.svc.projects.get(project.id)!, ...result };
  }

  /** Add individually picked photos to a project that already exists. */
  async addFiles(
    projectId: number,
    onProgress?: (p: ScanProgress) => void,
  ): Promise<{ added: number; scanned: number } | null> {
    const handles = await this.filePicker.pick('m3cull-files');
    if (handles === null) return null;

    const directory = new FileListDirectory(this.freeSourceName(projectId), handles);
    return this.scanFilesInto(projectId, directory, onProgress);
  }

  private async scanFilesInto(
    projectId: number,
    directory: FileListDirectory,
    onProgress?: (p: ScanProgress) => void,
  ): Promise<{ added: number; scanned: number }> {
    const sourceId = await this.sources.attachFiles(projectId, directory.name, directory);
    const result = await this.svc.scan.scan(projectId, sourceId, directory, onProgress);
    await this.persister.flush();
    return { added: result.added, scanned: result.scanned };
  }

  /**
   * Each hand-picked batch becomes its own source, because two batches picked on
   * different days are not the same pile of files.
   */
  private freeSourceName(projectId: number): string {
    const base = 'Selected photos';
    if (projectId === 0) return base;

    const taken = new Set(this.sources.list(projectId).map((source) => source.name));
    if (!taken.has(base)) return base;

    for (let n = 2; n < 1000; n++) {
      const candidate = `${base} (${n})`;
      if (!taken.has(candidate)) return candidate;
    }
    return `${base} (${taken.size + 1})`;
  }

  /** How many permission prompts reconnecting this source will cost. */
  promptCost(sourceId: number): Promise<number> {
    return this.sources.promptCost(sourceId);
  }

  /** Load the bundled sample shoot — no folder picker, works anywhere. */
  async importDemo(onProgress?: (p: ScanProgress) => void): Promise<ImportResult> {
    const directory = await HttpFsDirectory.load(
      `${import.meta.env.BASE_URL}demo`,
      `${import.meta.env.BASE_URL}demo/manifest.json`,
    );

    const project = this.svc.projects.create('Sample shoot', this.previewTargetPx);
    const result = await this.scanInto(project.id, directory, undefined, onProgress);

    // The sample shoot has no directory handle to persist, so remember that this
    // source is served over HTTP and re-attach it on the next boot.
    const source = this.sources.list(project.id).find((s) => s.name === directory.name);
    if (source) this.setSetting(DEMO_SOURCE_KEY, String(source.id));

    return { project: this.svc.projects.get(project.id)!, ...result };
  }

  private async scanInto(
    projectId: number,
    directory: FsDirectory,
    handle: FileSystemDirectoryHandle | undefined,
    onProgress?: (p: ScanProgress) => void,
  ): Promise<{ added: number; scanned: number }> {
    const sourceId = await this.sources.attach(projectId, directory.name, directory, handle);
    const result = await this.svc.scan.scan(projectId, sourceId, directory, onProgress);
    await this.persister.flush();
    return { added: result.added, scanned: result.scanned };
  }

  /**
   * Re-applies any decisions the journal caught after the last snapshot.
   *
   * This is what makes a decision survive a tab closed mid-cull. Entries are
   * idempotent, so replaying one already present in the snapshot is a no-op.
   */
  private async replayJournal(journal: DecisionJournal, media: MediaRepository): Promise<void> {
    const entries = journal.drain();
    if (entries.length === 0) return;

    let applied = 0;
    for (const entry of entries) {
      if (!media.byId(entry.id)) continue; // its project was deleted since
      if (entry.t === 'bucket') media.setBucket(entry.id, entry.v);
      else media.setRotation(entry.id, entry.v);
      applied++;
    }

    if (applied === 0) {
      journal.clear();
      return;
    }

    // Order matters: the journal is the only durable copy until the snapshot
    // lands, so clearing first would lose everything to a second quick reload.
    try {
      await this.persister.flush();
      journal.clear();
    } catch {
      // Leave the journal in place; the next boot replays it again.
    }
  }

  /** Reconnects the bundled sample shoot, which has no handle to restore. */
  private async restoreDemoSource(): Promise<void> {
    const stored = this.getSetting(DEMO_SOURCE_KEY);
    if (!stored) return;

    try {
      const directory = await HttpFsDirectory.load(
        `${import.meta.env.BASE_URL}demo`,
        `${import.meta.env.BASE_URL}demo/manifest.json`,
      );
      this.sources.attachEphemeral(Number(stored), directory);
    } catch {
      // The sample shoot is optional; a failure here must not stop the app.
    }
  }

  /**
   * Direct URLs to the bundled sample photographs.
   *
   * The home page hero shows real photographs being culled before anyone clicks
   * anything, and needs plain <img> sources rather than project media. Routed
   * through here so the UI never fetches the manifest itself (rule R1).
   */
  async sampleShootUrls(limit = 14): Promise<string[]> {
    try {
      const base = `${import.meta.env.BASE_URL}demo`;
      const res = await fetch(`${base}/manifest.json`);
      if (!res.ok) return [];

      const manifest = (await res.json()) as { files: { path: string }[] };
      return manifest.files
        .filter((file) => !file.path.includes('/'))
        .slice(0, limit)
        .map((file) => `${base}/${file.path}`);
    } catch {
      return []; // the hero degrades to its written promise
    }
  }

  listSources(projectId: number): SourceWithCount[] {
    return this.sources.list(projectId);
  }

  sourceAvailability(sourceId: number): Promise<SourceAvailability> {
    return this.sources.availability(sourceId);
  }

  /** Must be called from a click — browsers refuse ungestured permission prompts. */
  requestSourceAccess(sourceId: number): Promise<boolean> {
    return this.sources.requestAccess(sourceId);
  }

  /** Re-attaches a project whose folder was moved or is on a different machine. */
  async relinkSource(sourceId: number): Promise<boolean> {
    const picked = await this.picker.pick('read', 'm3cull-import');
    if (!picked) return false;
    this.sources.attachEphemeral(sourceId, picked.directory);
    return true;
  }

  /* ------------------------------------------------------------------ *
   * media
   * ------------------------------------------------------------------ */

  listMedia(projectId: number, opts: ListMediaOpts): Page<MediaItem> {
    return this.media.list(projectId, opts);
  }

  mediaIds(projectId: number, filter: MediaFilter): number[] {
    return this.media.idsFor(projectId, filter);
  }

  getMedia(id: number): MediaItem | null {
    return this.media.byId(id);
  }

  getMediaByIds(ids: number[]): MediaItem[] {
    return this.media.byIds(ids);
  }

  indexOfMedia(projectId: number, mediaId: number, filter: MediaFilter): number {
    return this.media.indexOf(projectId, mediaId, filter);
  }

  /** Would this item still be listed under this filter? Routed so the UI never imports domain. */
  matchesFilter(item: MediaItem, filter: MediaFilter): boolean {
    return matches(item, filter);
  }

  /* ------------------------------------------------------------------ *
   * culling
   * ------------------------------------------------------------------ */

  listBuckets(projectId: number): Bucket[] {
    return this.svc.buckets.list(projectId);
  }

  bucketByHotkey(projectId: number, hotkey: string): Bucket | null {
    return this.svc.buckets.byHotkey(projectId, hotkey);
  }

  assignBucket(mediaId: number, bucketId: number | null) {
    return this.svc.buckets.assign(mediaId, bucketId);
  }

  toggleBucket(mediaId: number, bucketId: number) {
    return this.svc.buckets.toggle(mediaId, bucketId);
  }

  revertBucket(change: { mediaId: number; from: number | null; to: number | null }): void {
    this.svc.buckets.revert(change);
  }

  reapplyBucket(change: { mediaId: number; from: number | null; to: number | null }): void {
    this.svc.buckets.reapply(change);
  }

  rotate(mediaId: number, degrees: number): number | null {
    return this.svc.rotation.rotate(mediaId, degrees);
  }

  /* ------------------------------------------------------------------ *
   * images
   * ------------------------------------------------------------------ */

  async thumbnailUrl(item: MediaItem): Promise<string | null> {
    const key = item.thumbKey ?? (await this.svc.thumbs.generate(item));
    if (!key) return null;

    const blob = await this.cache.getBlob(key);
    return blob ? URL.createObjectURL(blob) : null;
  }

  async previewUrl(item: MediaItem): Promise<string | null> {
    const blob = await this.svc.previews.blobFor(item, this.previewTargetPx);
    return blob ? URL.createObjectURL(blob) : null;
  }

  generateThumbnails(
    projectId: number,
    options: Parameters<ThumbnailService['generateMissing']>[1],
  ): Promise<number> {
    return this.svc.thumbs.generateMissing(projectId, options);
  }

  /* ------------------------------------------------------------------ *
   * review assist
   * ------------------------------------------------------------------ */

  /** Which frames are soft, which are picks. One call per shoot. */
  assistMarkers(projectId: number): AssistMarkers {
    return this.svc.assist.markers(projectId);
  }

  assistProgress(projectId: number): AssistProgress {
    return this.svc.assist.progress(projectId);
  }

  /** Score a shoot. Resumable, cancellable, and safe to call twice. */
  /** Begin a review that outlives the page that started it. */
  startAssist(projectId: number, deep: boolean): Promise<AssistProgress> {
    return this.runs.start(projectId, deep);
  }

  stopAssist(projectId: number): void {
    this.runs.stop(projectId);
  }

  /** The live run for this shoot, or null if none is under way. */
  assistRun(projectId: number): AssistProgress | null {
    return this.runs.current(projectId);
  }

  onAssistProgress(projectId: number, listener: (p: AssistProgress) => void): () => void {
    return this.runs.subscribe(projectId, listener);
  }

  runAssist(
    projectId: number,
    options?: Parameters<AssistService['review']>[1],
  ): Promise<AssistProgress> {
    return this.svc.assist.review(projectId, options);
  }

  /**
   * Whether Review Assist should run on every shoot without asking.
   *
   * Stored rather than inferred: a photographer who ticked "do this for every
   * import" has made a standing decision, and asking again would be a bug.
   */
  assistAlways(): boolean {
    return this.settings.getBoolean(ASSIST_ALWAYS_KEY, false);
  }

  /**
   * Awaits the write rather than letting the snapshot debounce catch it.
   *
   * These are deliberate, infrequent answers to a question the app asked. A
   * preference silently undone by closing the tab a moment later is worse than
   * never having offered it.
   */
  async setAssistAlways(value: boolean): Promise<void> {
    this.settings.setBoolean(ASSIST_ALWAYS_KEY, value);
    await this.persister.saveNow();
  }

  /**
   * What this shoot was told to do.
   *
   * 'unasked' means the popup has not been answered for it yet; 'declined' means
   * "not now", which is why the shoot page keeps a button rather than forgetting
   * the offer was ever made.
   */
  assistDecision(projectId: number): AssistDecision {
    if (this.assistAlways()) return 'deep';
    const raw = this.settings.get(assistDecisionKey(projectId));
    return raw === 'deep' || raw === 'declined' ? raw : 'unasked';
  }

  async setAssistDecision(projectId: number, decision: AssistDecision): Promise<void> {
    this.settings.set(assistDecisionKey(projectId), decision);
    await this.persister.saveNow();
  }

  /** True once every frame in the shoot carries a vector from the current model. */
  assistIsDeep(projectId: number): boolean {
    return this.svc.assist.isDeepReviewed(projectId);
  }

  resetAssist(projectId: number): void {
    this.svc.assist.reset(projectId);
    this.settings.set(assistDecisionKey(projectId), 'unasked');
  }

  /* ------------------------------------------------------------------ *
   * export
   * ------------------------------------------------------------------ */

  /** Pick a destination folder. Needs write permission, so it prompts. */
  async pickExportFolder(): Promise<FsDirectory | null> {
    const picked = await this.picker.pick('readwrite', 'm3cull-export');
    return picked?.directory ?? null;
  }

  runExport(
    options: ExportOptions,
    destination: FsDirectory,
    onProgress?: (p: ExportProgress) => void,
    signal?: { aborted: boolean },
  ): Promise<ExportProgress> {
    return this.svc.exporter.run(options, destination, onProgress, signal);
  }

  /* ------------------------------------------------------------------ *
   * housekeeping
   * ------------------------------------------------------------------ */

  cacheStats() {
    return this.svc.cache.stats();
  }

  sweepCache() {
    return this.svc.cache.sweepOrphans();
  }

  getSetting(key: string): string | null {
    return this.settings.get(key);
  }

  setSetting(key: string, value: string): void {
    this.settings.set(key, value);
    this.persister.touch();
  }

  save(): Promise<void> {
    return this.persister.flush();
  }
}
